/* PDP — How to use it.
   Paired with sections/product-how-to-use.liquid.

   Three jobs:
   1. Take the native controls off the video and put the design's play pill in
      their place. Done here rather than in the markup so that with no
      JavaScript the controls are still there and the film still plays.
   2. Add the selected variant to the cart from the button under the steps.
   3. Keep that button in step with the variant picker — the price in its
      label when the section shows one, and whether it can be pressed at all.
      The theme dispatches `product:variant-change` on `document` with the
      variant in `event.detail` — see assets/theme.js and assets/custom-events.js.

   Written in ES5 with no dependencies, matching assets/product-before-after.js
   and assets/quantity-breaks.js: this loads on every PDP and must not need a
   build step or a polyfill. */
(function () {
  'use strict';

  /* The section ships its own <script> tag, so a page carrying two of these
     loads - and runs - this file twice, binding the document listeners below
     twice with it. initAll() already walks every widget on the page, so one
     execution serves them all. Same guard as assets/product-before-after.js. */
  if (window.productHowToUseInitialized) return;
  window.productHowToUseInitialized = true;

  function find(root, selector) {
    return root.querySelector(selector);
  }

  function findAll(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  /* Only the parts belonging to this widget, never a nested one: a page can
     carry two of these sections (say, the mascara and the liner it pairs
     with). */
  function own(widget, selector) {
    return findAll(widget, selector).filter(function (node) {
      return node.closest('[data-htu]') === widget;
    });
  }

  /* ------------------------------------------------------------- video --- */

  function initVideo(widget) {
    var video = own(widget, '[data-htu-video]')[0];
    var pill = own(widget, '[data-htu-play]')[0];
    if (!video || !pill) return;

    /* The handover: the markup ships `controls` so a shopper without
       JavaScript is never stuck with a poster they cannot play. Now that the
       script is running, the pill takes over and the bar comes off. */
    video.removeAttribute('controls');
    pill.hidden = false;

    pill.addEventListener('click', function () {
      pill.hidden = true;
      video.setAttribute('controls', '');

      /* play() rejects on its own when the browser refuses - a data-saver
         setting, a tab that was never interacted with. Putting the pill back
         leaves the shopper something to press rather than a dead frame. */
      var played = video.play();
      if (played && typeof played.catch === 'function') {
        played['catch'](function () {
          pill.hidden = false;
          video.removeAttribute('controls');
        });
      }
    });

    /* Back to the poster-and-pill state once it has run through, so the panel
       reads as an invitation again rather than as a stopped player. Only on
       `ended`: restoring it on `pause` would snatch the controls away from
       someone who paused deliberately. */
    video.addEventListener('ended', function () {
      video.removeAttribute('controls');
      pill.hidden = false;
    });
  }

  /* -------------------------------------------------------------- price --- */

  /* `{ "<variant id>": { price, available } }`, rendered by Liquid: `money`
     knows the shop's format and its currency, and a second implementation in
     JavaScript would be a second thing to get wrong in a shop that sells in
     more than one of them. Parsed once and kept, because it never changes for
     the life of the page. */
  function variantMap(widget) {
    if (widget.htuVariants !== undefined) return widget.htuVariants;
    var slot = own(widget, '[data-htu-variants]')[0];
    var parsed = null;
    if (slot) {
      try {
        parsed = JSON.parse(slot.textContent);
      } catch (error) {
        parsed = null;
      }
    }
    widget.htuVariants = parsed;
    return parsed;
  }

  /* Keeps the button in step with the picker above: the price it shows, and
     whether it can be pressed at all. Without this the sold-out state would be
     frozen at whatever was selected when the page was rendered, and a shopper
     moving from a sold-out shade to one in stock would be left looking at a
     disabled "Sold out".

     A variant the map does not know - one added after the page was rendered,
     or a bundle app's synthetic id - is left alone rather than being treated
     as sold out, which would block a sale over a bookkeeping gap. */
  function updateForVariant(widget, variant) {
    var button = own(widget, '[data-htu-atc]')[0];
    if (!button) return;

    var map = variantMap(widget);
    var row = map && variant && map[String(variant.id)];
    if (!row) return;

    if (row.price) button.htuPrice = row.price;
    button.htuSoldOut = !row.available;
    button.disabled = !row.available;

    /* The label is left alone while a request is in flight: the add-to-cart
       handler owns it and `disabled` until it has put both back, and writing
       here would strip the "Adding…" copy mid-post. It will be redrawn from
       the state just recorded when that finishes. */
    if (button.getAttribute('aria-busy') !== 'true') drawLabel(button);
  }

  /* ---------------------------------------------------------------- label -- */

  function labelSlot(button) {
    return find(button, '[data-htu-button-text]') || button;
  }

  /* The idle label is rebuilt from the button's own attributes rather than
     captured from the DOM and replayed. Capturing it meant two bugs: a second
     press inside the two seconds "Added to Bag" is up captured *that* as the
     idle text, leaving the button saying it for the rest of the page's life;
     and a page rendered on a sold-out variant captured "Sold out", so a shopper
     switching to a shade in stock got an enabled button that still said it. */
  function drawLabel(button) {
    var slot = labelSlot(button);

    if (button.htuSoldOut) {
      slot.textContent = button.getAttribute('data-soldout-label') || '';
      return;
    }

    slot.textContent = button.getAttribute('data-label') || '';

    if (button.getAttribute('data-htu-show-price') === 'true' && button.htuPrice) {
      var price = document.createElement('span');
      price.className = 'htu__button-price';
      price.textContent = button.htuPrice;
      slot.appendChild(price);
    }
  }

  /* What the page was rendered with, read once so `drawLabel` can reproduce it
     without the markup ever having to be parsed back out of the DOM. */
  function readInitialState(widget) {
    var button = own(widget, '[data-htu-atc]')[0];
    if (!button) return;
    var price = find(button, '.htu__button-price');
    if (price) button.htuPrice = price.textContent.trim();
    button.htuSoldOut = button.disabled;
  }

  /* ---------------------------------------------------------- add to cart -- */

  function setError(widget, message) {
    var slot = own(widget, '[data-htu-error]')[0];
    if (slot) slot.textContent = message || '';
  }

  /* Merchant-facing copy is a section setting, matching the quantity breaks
     block and the before/after section - so both strings stay translatable
     through Translate & Adapt instead of being English hard-coded in an asset.
     The literals are the last-resort fallback for a section saved before the
     settings existed. */
  function errorText(widget, name, fallback) {
    return widget.getAttribute('data-htu-' + name) || fallback;
  }

  /* The PDP's own product form - never this section's. Ours matches
     `form[action*="/cart/add"]` too, and on a page where the real form is
     missing that selector would otherwise find ours and read back the variant
     the page was rendered with instead of refusing. */
  function productForm(widget) {
    var forms = findAll(document, '[data-product-form], form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      if (!widget.contains(forms[i])) return forms[i];
    }
    return null;
  }

  function addToCart(widget, button) {
    var form = productForm(widget);
    var idInput = form && find(form, '[name="id"]');
    var variantId = idInput && idInput.value;

    if (!variantId) {
      setError(widget, errorText(widget, 'choose-error', 'Choose an option above first.'));
      return;
    }

    /* Built explicitly rather than serialising the form, so a bundle app's own
       hidden inputs cannot ride along — the same reasoning as
       assets/quantity-breaks.js. Line-item properties are kept, because they
       are the shopper's input. */
    var body = 'id=' + encodeURIComponent(variantId) + '&quantity=1';
    findAll(form, '[name^="properties"]').forEach(function (input) {
      if (input.type === 'checkbox' && !input.checked) return;
      body += '&' + encodeURIComponent(input.name) + '=' + encodeURIComponent(input.value);
    });

    setError(widget, '');
    var slot = labelSlot(button);
    var busy = button.getAttribute('data-busy-label');

    /* A restore still pending from the previous add would otherwise fire part
       way through this one and put the idle label back under "Adding…". */
    if (button.htuRestoreTimer) {
      window.clearTimeout(button.htuRestoreTimer);
      button.htuRestoreTimer = null;
    }

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    if (busy) slot.textContent = busy;

    /* `drawLabel`, not a captured string: the picker may have moved to another
       shade - or to a sold-out one - while the request was in flight, and the
       button has to come back showing the state it is in now, not the one it
       was in when it was pressed. */
    var restore = function () {
      button.removeAttribute('aria-busy');
      button.disabled = !!button.htuSoldOut;
      drawLabel(button);
    };

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    }).then(function (response) {
      return response.json().then(function (data) { return { ok: response.ok, data: data }; });
    }).then(function (result) {
      if (!result.ok) {
        /* Shopify's own description is already in the shopper's language. */
        restore();
        setError(widget, (result.data && result.data.description) || errorText(widget, 'add-error', 'Sorry, that could not be added.'));
        return;
      }

      var done = button.getAttribute('data-done-label');
      if (done) {
        slot.textContent = done;
        button.removeAttribute('aria-busy');
        button.disabled = false;
        button.htuRestoreTimer = window.setTimeout(function () {
          button.htuRestoreTimer = null;
          restore();
        }, 2000);
      } else {
        restore();
      }

      /* The theme refreshes its cart count off this event, and the header icon
         is what actually opens its drawer. */
      document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
      var icon = find(document, '[data-js-cart-icon]');
      if (icon) icon.click();
    })['catch'](function () {
      restore();
      setError(widget, errorText(widget, 'add-error', 'Sorry, that could not be added.'));
    });
  }

  /* --------------------------------------------------------------- init --- */

  function init(widget) {
    if (widget.hasAttribute('data-htu-ready')) return;
    widget.setAttribute('data-htu-ready', 'true');

    initVideo(widget);
    readInitialState(widget);

    /* Bound to the form's submit, not the button's click: the button is a real
       submit button so the section still adds to the cart with no JavaScript,
       and submit is the one event that catches both the click and the Enter
       key on a focused button. Binding click as well would run the add twice
       for every mouse press. */
    widget.addEventListener('submit', function (event) {
      var form = event.target.closest('.htu__form');
      if (!form || form.closest('[data-htu]') !== widget) return;
      var button = find(form, '[data-htu-atc]');
      if (!button) return;
      event.preventDefault();
      addToCart(widget, button);
    });
  }

  function initAll(root) {
    findAll(root || document, '[data-htu]').forEach(init);
  }

  document.addEventListener('product:variant-change', function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant) return;
    findAll(document, '[data-htu]').forEach(function (widget) {
      updateForVariant(widget, variant);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('quickview:loaded', function () { initAll(); });
})();
